import { BaseModel } from './base.model';
import { AppDate } from './date.model';
import { Attribute } from './attribute.model';
import { generateUUID, isEmptyArray, normalizeTrackingData, LooseInput } from '../shared/utils';
import { TrackingNumber } from '../interfaces/tracking-number';
import { Address } from './address.model';
import { allocationGroupIdOf, OrderPresetAddress } from './order-preset-address';
import { User } from './user.model';
import { CostCenter } from './cost-center.model';
import { AddressType } from '../enums/address-type';
import { ContactDetails } from '../interfaces/contact-details';
import { DiscountCode } from './discount-code.model';
import { Product } from './product.model';
import {AllocationGroup, InventoryAllocationDelivery} from '../interfaces/allocation-group';


export const IMS_BTI = 'IMS - BTI';

export const WSLR = 'WSLR'

export interface OrderItemDeliveryAddressRef {
    addr_id?: number;
    addr_type?: string | AddressType;
    entity_id?: number;
}

type OrderItemDeliveryInput = LooseInput<OrderItemDelivery> & {
    data?: OrderItemDeliveryAddressRef | InventoryAllocationDelivery[];
    allocation_groups?: InventoryAllocationDelivery[];
    cost_center?: object;
    discount_code?: object;
    uuid?: string;
    internal_order?: string;
    internal_order_id?: string;
    wbs?: string;
    contact_details?: ContactDetails[];
    app_coupon_id?: number;
    child_data?: string;
    chili_project_id?: string;
};

export class OrderItemDelivery extends BaseModel {
    public id: number;
    public item_id: number;
    public addr_id: number;
    public cache_id: number;
    public entity_id: number;
    public status: string;
    public type: string;
    public method: string;
    public quantity: number;
    public weight: number;
    public price: number;
    public label: string;
    public street_1: string;
    public street_2: string;
    public city: string;
    public state: string;
    public zip_code: string;
    public country_code: string;
    public residential: boolean;
    public tracking_number: unknown;
    public shipped_at: AppDate;
    public data: OrderItemDeliveryAddressRef;

    public attrs: Array<Attribute>;
    public addr_type: string;
    public address_type: AddressType;

    // derived properties
    public method_formatted: string;
    public tracking_url: string;
    public formattedTracking: string;
    public unchanged = true;
    public deliver_to: string;
    public orderAttention: string;
    public cost_center: CostCenter;
    public uuid: string;
    public is_uneligible: boolean;
    public isAdditional = false;
    // pendingDelete = false; //  true when user removed a delivery

    public internal_order: string;
    public internal_order_id: string;
    public wbs: string;
    public contact_details: ContactDetails[];

    public app_coupon_id: number;
    public discount_code: DiscountCode | null;
    public eudr_applicable: boolean;

    public chili_data: string; // json chili  document for customization
    public chili_project_id: string;
    public chili_output: string;   // url of chili  output  can be image or pdf
    public chili_output_thumbnail; // thumbnail of chili  output image
    public chili_design_name: string|null; // design name

    public allocation_group_id: number;  //  allocation group id is used only for BW  products
    public allocation_group_label: string; //  allocation group label is used only for BW  products
    public allocation_groups: InventoryAllocationDelivery[];

    constructor(data: OrderItemDeliveryInput = {}) {
        super(data, {
            num: ['id', 'item_id', 'cache_id', 'entity_id', 'quantity', 'weight', 'price'],
            bool: ['residential', 'eudr_applicable'],
            date: ['shipped_at']
        });

        const addrRef = data.data as OrderItemDeliveryAddressRef;
        this.data = {
            addr_id: this.addr_id,
            addr_type: this.addr_type,
        };
        if (this.address_type === AddressType.Corporate) {
            this.data.entity_id = this.entity_id;
        }

        if (addrRef && addrRef.addr_id) {
            this.data = {
                addr_id: addrRef.addr_id,
                addr_type: addrRef.addr_type,
            };
            if (addrRef.addr_type === AddressType.Corporate) {
                this.data.entity_id = addrRef.entity_id;
            }
        }


        if (data.cost_center) {
            this.cost_center = new CostCenter(data.cost_center);
        }

        this.uuid = data.uuid || generateUUID();

        if (!this.deliver_to && this.isIMS) {
            this.deliver_to = this.IMSAttention;
        }

        if (data.is_uneligible) {
            this.is_uneligible = !!data.is_uneligible;
        } else {
            this.is_uneligible = false;
        }


        if (this.label?.startsWith(WSLR)) {
            const pos = this.label.indexOf('-');
            if (pos !== -1) {
                this.label = this.label.substring(pos + 1);
            }
        }

        this.internal_order = data?.internal_order || '';
        this.internal_order_id = data?.internal_order_id || '';
        this.wbs = data?.wbs || '';
        this.contact_details = data?.contact_details || [];
        this.app_coupon_id = data?.app_coupon_id;
        this.discount_code = data?.discount_code ? new DiscountCode(data.discount_code) : null;

        if (!this.chili_data) {
          this.chili_data = data?.child_data;
        }

        if (!this.chili_project_id) {
          this.chili_project_id = data?.chili_project_id;
        }

        // Always rebuild allocation_groups as an isolated copy. BaseModel's Object.assign(this, data)
        // copies the array (and its element objects) BY REFERENCE, so without this a working copy
        // (e.g. new OrderItem(cachedItem)) would share allocation_groups with the source — and
        // saveAllocationGroup() would mutate the cached order in OrderService.orders in place, even
        // for unsaved/failed edits.
        const sourceAllocationGroups = data?.allocation_groups
            ? data.allocation_groups
            : (!isEmptyArray(data?.data) ? data.data as InventoryAllocationDelivery[] : []);
        this.allocation_groups = (sourceAllocationGroups || []).map(ag => ({ ...ag }));
    }

    public getCSZ() {
        let result = this.city;
        if (this.state) {
            result += ', ' + this.state;
        }
        if (this.zip_code) {
            result += ', ' + this.zip_code;
        }
        return result;
    }

    get normalizedTrackingNumbers(): TrackingNumber[] {
        return normalizeTrackingData(this.tracking_number);
    }


    public setAddress(
        address: Address,
        currentUser: User,
        userRoles: { canOrder: boolean; isEmployee: boolean },
        product?: Product
    ) {
        if (!userRoles.canOrder || !address) {
            return {success: false, errorMessage: 'Cannot set address: insufficient permissions or invalid address'};
        }

        this.addr_id = address.id;
        this.cache_id = address.id;
        this.label = address.label;
        this.street_1 = address.street_1;
        this.city = address.city;
        this.state = address.state;
        this.country_code = address.country_code;
        this.zip_code = address.zip_code;
        this.addr_type = address.type;
        this.address_type = address.address_type;
        this.data.addr_id = address.id;
        this.data.addr_type = address.type;
        this.eudr_applicable = address.eudr_applicable;

        // A warehouse address taken from the order preset addresses owns its allocation group -
        // the same warehouse can be present once per group, and the group is what tells the rows apart,
        // so it has to be carried over rather than dropped. For every other address there is no group.
        const presetAllocationGroupId = allocationGroupIdOf(address);
        if (presetAllocationGroupId != null) {
            this.allocation_group_id = presetAllocationGroupId;
        } else {
            // reset allocation group id when address changes
            this.allocation_group_id = undefined;
            this.allocation_group_label = undefined;
        }

        if (address.address_type === AddressType.Corporate) {
            this.data.entity_id = address.entity_id;
        }
        this.contact_details = address.contact_details || [];

        if (address.app_coupon_id) {
            this.app_coupon_id = address.app_coupon_id;
        }

        if (product) {
            const eudrCheck = this.hasEudrRestriction(product, address);
            return eudrCheck.hasRestriction
                ? {success: true, errorMessage: eudrCheck.errorMessage, footerMessage: eudrCheck.footerMessage}
                : {success: false};
        } else {
            return {success: false};
        }
    }

    public getAddress(): Address {
        const address = new Address({
            id: this.addr_id,
            address_type: this.address_type,
            label: this.label,
            street_1: this.street_1,
            city: this.city,
            state: this.state,
            country_code: this.country_code,
            zip_code: this.zip_code,
            type: this.addr_type,
            // this.data.entity_id is empty for every delivery the API loads with a data.addr_id: the
            // constructor rebuilds this.data from scratch there and only restores the entity under
            // `data.data.addr_type === AddressType.Corporate`, which compares the addr_type string
            // against a number and is never true. The delivery's own entity_id is the same value, so
            // it stands in - without it a delivery-derived corporate address carries no entity while
            // the same address, built from the address book, does.
            entity_id: this.address_type === AddressType.Corporate
                ? (this.data.entity_id ?? this.entity_id)
                : undefined,
            contact_details: this.contact_details,
            app_coupon_id: this.discount_code?.id || this.app_coupon_id,
            eudr_applicable: this.eudr_applicable
        });

        // a warehouse delivery belongs to one allocation group, and that pair is what makes the column.
        // The group is set in place rather than through withAllocationGroup: that helper copies the
        // address so two rows never share one instance, and this one was just built here and is shared
        // with nobody. findUniqueShipmentAddresses calls this for every delivery on every read, so the
        // copy would double the allocations on the hottest path of the grid.
        if (this.allocation_group_id != null) {
            (address as OrderPresetAddress).allocation_group_id = this.allocation_group_id;
        }

        return address;
    }

    public prepareToSave(): object {
        const obj = {
            id: this.id,
            type: this.type,
            method: this.method,
            quantity: this.quantity,
            addr_id: this.addr_id,
            address_type: this.address_type,
            deliver_to: this.deliver_to || '',
            cost_center: this.cost_center ? this.cost_center : null,
            is_uneligible: this.is_uneligible ? 1 : 0,
            data: {
                addr_id: this.data.addr_id || this.addr_id,
                addr_type: this.data.addr_type,
            },
        }
        if (this.address_type === AddressType.Corporate) {
            obj['data']['entity_id'] = this.data.entity_id;
        }

        if (this.internal_order) {
            obj['internal_order'] = this.internal_order.trim();
            obj['internal_order_id'] = this.internal_order_id;
        }
        if (this.wbs) {
            obj['wbs'] = this.wbs.trim();
        }

        // in some case  address_type is not set , this is  very temporary solution and can be stooped working after address book merge
        if (!obj['address_type']) {
            obj['address_type'] = obj.data.addr_type === 'OTHER_ADDR' ? 1 : 2
        }
        if (!isEmptyArray(this.contact_details)) {
            obj['contact_details'] = this.contact_details
        }

        if (obj['address_type'] === AddressType.Corporate) {
            obj.data['entity_id'] = this.data.entity_id;
        }

        if (this.app_coupon_id || this.discount_code) {
            obj['app_coupon_id'] = this.discount_code?.id || this.app_coupon_id;
        }

          if (this.chiliProjectId) {
            obj['chili_project_id'] = this.chiliProjectId;
            obj['chili_data'] = this.chiliDocument;
            obj['chili_design_name'] = this.chiliDesignName;
          }

          if (this.allocation_group_id) {
            obj['allocation_group_id'] = this.allocation_group_id;
            obj['allocation_group_label'] = this.allocation_group_label;
          }

          if (!isEmptyArray(this.allocation_groups)) {
              obj['data']['allocation_groups'] = this.allocation_groups;
          }

        return obj;
    }

    get isInternational(): boolean {
        return this.country_code && this.country_code.toLowerCase() !== 'us';
    }

    get addressDefined(): boolean {
        return this.addr_id >= 0;   // for imported addresses addr_id can be 0
    }

    get isIMS(): boolean {
        return this.label && this.label.includes(IMS_BTI);
    }

    get isWSLR(): boolean {
        return this.label && this.label.indexOf(WSLR) === 0;
    }

    get IMSAttention() {
        if (!this.label) {
            return '';
        }
        return this.label;
    }

    get wslrLabel() {
        if (!this.isWSLR) {
            return this.label;
        }

        return this.labelWithoutWslrPrefix;
    }

    get labelWithoutWslrPrefix(): string {
        if (!this.label) {
            return '';
        }
        if (this.label.startsWith('WSLR')) {
            const pos = this.label.indexOf('-');
            if (pos !== -1) {
                return this.label.substring(pos + 1);
            }
        }

        return this.label;
    }


    get internalOrder(): string {
        return this.wbs || this.internal_order || '';
    }

    public get uniqueAddressId(): string {
        if (!this.addr_id) {
            return null;
        }
        return `${this.addr_id}-${this.address_type}`;
    }

    /**
     * Matches the key of the address this delivery belongs to (see orderAddressKey). A warehouse can
     * be present several times, once per allocation group, so the address alone does not identify the column.
     */
    public get orderAddressKey(): string {
        const addressKey = this.uniqueAddressId;
        if (!addressKey) {
            return null;
        }
        return this.allocation_group_id != null ? `${addressKey}-${this.allocation_group_id}` : addressKey;
    }

    public get mainOrFirstContactName(): string {
        if (!this.addr_id) {
            return '';
        }

        const address = this.getAddress();
        return address?.mainOrFirstContactName || '';

    }

    get discountCodeId(): number {
        return this.discount_code ? this.discount_code.id : this.app_coupon_id;
    }

    get chiliProjectId(): string {
      return this.chili_project_id;
    }

    get chiliDocument(): string {
      return this.chili_data;
    }

    get isCustomized(): boolean {
      return !!this.chiliProjectId && !!this.chiliDocument;
    }


    get chiliOutput(): string {
      return this.chili_output;
    }

    get chiliOutputThumbnail(): string {
      return this.chili_output_thumbnail;
    }

    get isChiliOutputImage(): boolean {
      if (!this.chiliOutput) {
        return false;
      }

      return !!this.chiliOutputThumbnail;

    }

    get chiliDesignName(): string|null {
      return this.chili_design_name;
    }

    public hasEudrRestriction(product: Product, address: Address): {
        hasRestriction: boolean;
        errorMessage?: string;
        footerMessage?: string;
    } {
        const hasRestriction = product?.eudr_checks?.canAddToCart === false && address?.eudr_applicable === true;
        if (hasRestriction) {
            return {
                hasRestriction: true,
                errorMessage: product.eudr_checks.errorMessage,
                footerMessage: product.eudr_checks.footerMessage
            };
        }
        return {hasRestriction: false};
    }


    public  saveAllocationGroup(skuId: number, allocationGroup: AllocationGroup) {
        const existingAllocation = this.allocation_groups?.find(ag => ag.sku_id === skuId);
        if (existingAllocation) {
            // update existing
            existingAllocation.allocation_group_id = allocationGroup.allocation_group_id;
            existingAllocation.allocation_group_label = allocationGroup.allocation_group_label;
        } else {
            // add new
            if (!this.allocation_groups) {
                this.allocation_groups = [];
            }
            this.allocation_groups.push({
                sku_id: skuId,
                allocation_group_id: allocationGroup.allocation_group_id,
                allocation_group_label: allocationGroup.allocation_group_label,
                quantity: 0
            });
        }
    }

    public saveAllocationGroupQuantity(skuId: number, quantity: number) {
        const existingAllocation = this.allocation_groups?.find(ag => ag.sku_id === skuId);
        if (existingAllocation) {
            existingAllocation.quantity = quantity;
        } else {
            // add new
            if (!this.allocation_groups) {
                this.allocation_groups = [];
            }
            this.allocation_groups.push({
                sku_id: skuId,
                allocation_group_id: null,
                allocation_group_label: null,
                quantity: quantity
            });
        }
    }


}
